import { useState } from "react";
import analyticsAdminApi from "../api/analytics/analyticsAdminApi";
import { CreateAnalyticsPageFormData } from "../types/analytics/admin/CreateAnalyticsPageFormData";
import { CreateAnalyticsPageApiResponse } from "../types/analytics/admin/CreateAnalyticsPageApiResponse";
import { CreateAnalyticsSimpleWidgetFormData } from "../types/analytics/admin/CreateAnalyticsSimpleWidgetFormData";
import { CreateAnalyticsSimpleWidgetApiResponse } from "../types/analytics/admin/CreateAnalyticsSimpleWidgetApiResponse";
import {
  GetWidgetsForPageApiResponse,
  WidgetForAnalyticsPage,
} from "../types/analytics/admin/GetWidgetsForPageApiResponse";
import { AnalyticsPageAdminDto } from "../types/analytics/admin/AnalyticsAdminModels";

interface UseAnalyticsAdminApiResult {
  newPageLoading: boolean;
  newPageError: any;
  newPageResponse: CreateAnalyticsPageApiResponse | null;
  newSimpleWidgetLoading: boolean;
  newSimpleWidgetError: any;
  newSimpleWidgetResponse: CreateAnalyticsSimpleWidgetApiResponse | null;
  addNewPage: (
    data: CreateAnalyticsPageFormData,
    onSuccess?: (data: CreateAnalyticsPageApiResponse) => void,
    onError?: (error: any) => void
  ) => void;
  addNewSimpleWidget: (
    data: CreateAnalyticsSimpleWidgetFormData,
    onSuccess?: (data: CreateAnalyticsSimpleWidgetApiResponse) => void,
    onError?: (error: any) => void
  ) => void;
  getWidgetsForPage: (
    pageId: number,
    onSuccess?: (data: any) => void,
    onError?: (error: any) => void
  ) => void;
  widgetsForPageLoading: boolean;
  widgetsForPageError: any;
  widgetsForPageResponse: WidgetForAnalyticsPage[];
  getPages: (
    onSuccess?: (data: AnalyticsPageAdminDto[]) => void,
    onError?: (error: any) => void
  ) => void;
  pagesLoading: boolean;
  pagesError: any;
  pagesResponse: AnalyticsPageAdminDto[];
}

export function useAnalyticsAdminApi(
  authToken: string | undefined
): UseAnalyticsAdminApiResult {
  const [newPageLoading, setNewPageLoading] = useState(false);
  const [newPageError, setNewPageError] = useState<any>(null);
  const [newPageResponse, setNewPageResponse] =
    useState<CreateAnalyticsPageApiResponse | null>(null);

  const [newSimpleWidgetLoading, setNewSimpleWidgetLoading] = useState(false);
  const [newSimpleWidgetError, setNewSimpleWidgetError] = useState<any>(null);
  const [newSimpleWidgetResponse, setNewSimpleWidgetResponse] =
    useState<CreateAnalyticsSimpleWidgetApiResponse | null>(null);

  // State for getWidgetsForPage
  const [widgetsForPageLoading, setWidgetsForPageLoading] = useState(false);
  const [widgetsForPageError, setWidgetsForPageError] = useState<any>(null);
  const [widgetsForPageResponse, setWidgetsForPageResponse] = useState<
    WidgetForAnalyticsPage[]
  >([]);

  const api = new analyticsAdminApi(authToken);
  // State for getPages
  const [pagesLoading, setPagesLoading] = useState(false);
  const [pagesError, setPagesError] = useState<any>(null);
  const [pagesResponse, setPagesResponse] = useState<AnalyticsPageAdminDto[]>([]);

  // Get pages
  const getPages = (
    onSuccess?: (data: AnalyticsPageAdminDto[]) => void,
    onError?: (error: any) => void
  ) => {
    setPagesLoading(true);
    setPagesError(null);
    setPagesResponse([]);
    api.getPages(
      (res: AnalyticsPageAdminDto[]) => {
        setPagesLoading(false);
        setPagesResponse(res);
        if (onSuccess) onSuccess(res);
      },
      (err: any) => {
        setPagesLoading(false);
        setPagesError(err);
        if (onError) onError(err);
      }
    );
  };
  // Get widgets for a page
  const getWidgetsForPage = (
    pageId: number,
    onSuccess?: (data: GetWidgetsForPageApiResponse) => void,
    onError?: (error: any) => void
  ) => {
    setWidgetsForPageLoading(true);
    setWidgetsForPageError(null);
    setWidgetsForPageResponse([]);
    if (api.getWidgetsForPage) {
      api.getWidgetsForPage(
        pageId,
        (res: GetWidgetsForPageApiResponse) => {
          setWidgetsForPageLoading(false);
          setWidgetsForPageResponse(
            res.success && res.widgets ? res.widgets : []
          );
          if (onSuccess) onSuccess(res);
        },
        (err: any) => {
          setWidgetsForPageLoading(false);
          setWidgetsForPageError(err);
          if (onError) onError(err);
        }
      );
    } else {
      // fallback: simulate error if not implemented
      setWidgetsForPageLoading(false);
      setWidgetsForPageError("getWidgetsForPage not implemented");
      if (onError) onError("getWidgetsForPage not implemented");
    }
  };

  const addNewPage = (
    data: CreateAnalyticsPageFormData,
    onSuccess?: (data: CreateAnalyticsPageApiResponse) => void,
    onError?: (error: any) => void
  ) => {
    setNewPageLoading(true);
    setNewPageError(null);
    setNewPageResponse(null);
    api.addNewPage(
      data,
      (res) => {
        setNewPageLoading(false);
        setNewPageResponse(res);
        if (onSuccess) onSuccess(res);
      },
      (err) => {
        setNewPageLoading(false);
        setNewPageError(err);
        if (onError) onError(err);
      }
    );
  };

  const addNewSimpleWidget = (
    data: CreateAnalyticsSimpleWidgetFormData,
    onSuccess?: (data: CreateAnalyticsSimpleWidgetApiResponse) => void,
    onError?: (error: any) => void
  ) => {
    setNewSimpleWidgetLoading(true);
    setNewSimpleWidgetError(null);
    setNewSimpleWidgetResponse(null);
    api.addNewSimpleWidget(
      data,
      (res) => {
        setNewSimpleWidgetLoading(false);
        setNewSimpleWidgetResponse(res);
        if (onSuccess) onSuccess(res);
      },
      (err) => {
        setNewSimpleWidgetLoading(false);
        setNewSimpleWidgetError(err);
        if (onError) onError(err);
      }
    );
  };

  return {
    newPageLoading,
    newPageError,
    newPageResponse,
    newSimpleWidgetLoading,
    newSimpleWidgetError,
    newSimpleWidgetResponse,
    addNewPage,
    addNewSimpleWidget,
    getWidgetsForPage,
    widgetsForPageLoading,
    widgetsForPageError,
    widgetsForPageResponse,
    getPages,
    pagesLoading,
    pagesError,
    pagesResponse,
  };
}
